/**
 * <WhatsUnlockedBanner> — one-time post-upgrade welcome on the Dashboard.
 *
 * Shows when:
 *   - User has a paid effective tier (Steel/Damascus, or admin/staff)
 *   - AND user.welcomed_for_tier !== effectiveTier(user)
 *     (so a Steel → Damascus upgrade re-shows with Damascus content).
 *
 * Dismissal POSTs /api/auth/me/dismiss-upgrade-welcome and refreshes
 * AuthContext so the banner disappears immediately without a full reload.
 *
 * Designed to be dropped at the top of the Dashboard, above the metric cards.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { effectiveTier, TIER_LABEL } from '../lib/tier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_GRADIENTS = {
  pro:      'linear-gradient(135deg, #64748B 0%, #0F172A 100%)',
  pro_plus: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
};

// What's unlocked, per tier. Kept short and customer-y — these are bullets
// the user can scan in 5 seconds and feel "ah, that's what I paid for".
const UNLOCKED_BULLETS = {
  pro: [
    'Auto-reconcile from a bank statement (CSV / Excel upload)',
    'Spending category donut on the Dashboard',
    '18-month future balance forecast',
    'Email any export — PDF, Excel, or CSV — to anyone',
    'Unlimited accounts, recurring transactions, and CSV imports',
  ],
  pro_plus: [
    'Everything in Steel, plus:',
    '36-month future balance forecast (3 years out)',
    'Early access to new features — 2-week head start',
    'Shared accounts (planned) — partners and accountants',
    'Direct Open Banking links + multi-currency tracking (planned)',
  ],
};

const WhatsUnlockedBanner = () => {
  const { user, checkAuth } = useAuth();
  const [dismissing, setDismissing] = useState(false);
  const [optimisticallyHidden, setOptimisticallyHidden] = useState(false);

  if (!user) return null;
  const tier = effectiveTier(user);
  if (tier === 'free') return null;
  if (user.welcomed_for_tier === user.tier) return null;
  if (optimisticallyHidden) return null;

  const tierName = TIER_LABEL[tier] || 'Pro';
  const grad = TIER_GRADIENTS[tier] || TIER_GRADIENTS.pro;
  const bullets = UNLOCKED_BULLETS[tier] || UNLOCKED_BULLETS.pro;

  const dismiss = async () => {
    setDismissing(true);
    // Hide immediately — no need to wait for the round-trip; backend POST is
    // idempotent and the AuthContext refresh below makes the state durable.
    setOptimisticallyHidden(true);
    try {
      await axios.post(`${API}/auth/me/dismiss-upgrade-welcome`, {}, { withCredentials: true });
      try { await checkAuth(); } catch { /* AuthContext error toast handles it */ }
    } catch {
      // If POST fails, unhide so the user can dismiss again (or it'll just
      // reappear on next page load — no data loss).
      setOptimisticallyHidden(false);
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div
      className="relative rounded-xl text-white p-6 mb-6 shadow-[0_8px_30px_rgb(0,82,255,0.12)]"
      style={{ backgroundImage: grad }}
      data-testid="whats-unlocked-banner"
    >
      <button
        type="button"
        onClick={dismiss}
        disabled={dismissing}
        aria-label="Dismiss what's unlocked banner"
        data-testid="whats-unlocked-dismiss-x"
        className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors text-white/70 hover:text-white"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 mb-2">
            Welcome to {tierName}
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight">
            Here&apos;s what you just unlocked.
          </h2>
          <ul className="mt-3 space-y-1.5">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-white/90" data-testid={`whats-unlocked-bullet`}>
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/80" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Button
              type="button"
              onClick={dismiss}
              disabled={dismissing}
              className="bg-white text-[#020617] hover:bg-white/90 border-0"
              data-testid="whats-unlocked-got-it"
            >
              {dismissing ? 'Saving…' : 'Got it'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsUnlockedBanner;
